import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import { hydrateCatalog } from "../../data/local/catalog";
import { money } from "../../domain/money/money";
import { ConfirmDestructive } from "../components/ConfirmDestructive";

/**
 * Menu management (US6: T193, T194, T195, T199).
 *
 * Tabs, sub-categories, items and their sizes, editable from the terminal.
 *
 * WHY IT LOOKS LIKE THE TILL rather than an admin console: it is used on the
 * same tablet, often mid-service, by someone who has thirty seconds between
 * customers. Every control clears the touch minimum and nothing depends on a
 * keyboard beyond typing a name and a price.
 *
 * WHAT IT DOES NOT DO, deliberately. It never deletes. An item goes "sold out"
 * (still on the menu, greyed) or is retired (off the till, still in the
 * database) — because every sale ever taken names an item, and a report that
 * groups by item has to keep working for as long as those sales are kept.
 *
 * Prices are changed through POST /catalog/prices, which closes the version in
 * force and opens a new one. Nothing here can overwrite a price, so a receipt
 * printed last week still shows what that customer actually paid.
 */

interface Category {
  id: string;
  name: string;
  parentId: string | null;
  sortOrder: number;
}

interface MenuItem {
  id: string;
  categoryId: string;
  name: string;
  available: boolean;
  sortOrder: number;
  statutoryDiscountEligible: boolean;
}

interface Variant {
  id: string;
  menuItemId: string;
  name: string;
  price: number;
  sortOrder: number;
}

interface Catalog {
  categories: Category[];
  menuItems: MenuItem[];
  variants: Variant[];
}

export interface MenuManagerProps {
  token: string;
  currency: CurrencyFormat;
  onClose(): void;
}

/**
 * How many items a page holds before the dialog is measured.
 *
 * Only ever on screen for one frame — `useMeasuredPageSize` replaces it with
 * what actually fits. A guessed constant was the first attempt and it was
 * wrong on three of the four screen sizes tested: five items fit a tablet in
 * portrait and overflowed it in landscape, which is the same "cannot reach
 * the rest of the list" bug wearing a different hat.
 */
const DEFAULT_PAGE_SIZE = 5;

/** Never fewer than one item per page, and never a page nobody can take in. */
const MIN_PAGE_SIZE = 1;
const MAX_PAGE_SIZE = 12;

interface MenuPage {
  group: { id: string; name: string; direct: boolean };
  items: MenuItem[];
  /** Which chunk of its group this is, 1-based, and how many there are. */
  part: number;
  parts: number;
}

/**
 * Cut a tab's groups into pages of at most PAGE_SIZE items.
 *
 * A group ALWAYS gets at least one page, even with nothing in it: an empty
 * sub-category still needs somewhere to show its "+ Item" button, or it can
 * never be filled. The exception is a tab's direct-items bucket, which is not
 * a real category — with nothing in it there is nothing to say.
 */
function paginate(
  groups: Array<{ id: string; name: string; direct: boolean }>,
  menuItems: MenuItem[],
  pageSize: number,
): MenuPage[] {
  const pages: MenuPage[] = [];

  for (const group of groups) {
    const items = menuItems
      .filter((i) => i.categoryId === group.id)
      .sort((a, b) => a.sortOrder - b.sortOrder);

    if (group.direct && items.length === 0) continue;

    const parts = Math.max(Math.ceil(items.length / pageSize), 1);
    for (let part = 0; part < parts; part++) {
      pages.push({
        group,
        items: items.slice(part * pageSize, (part + 1) * pageSize),
        part: part + 1,
        parts,
      });
    }
  }

  return pages;
}

/** Pesos in the box, centavos in the database. */
function toCentavos(input: string): number | null {
  const value = Number(input.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

function toPesos(centavos: number): string {
  return (centavos / 100).toFixed(2);
}

/**
 * How many item rows fit on one page of this dialog, on this screen.
 *
 * Measured from what is actually rendered rather than assumed, because the
 * budget depends on things a constant cannot know: the screen, the
 * orientation, the reader's font size, and whether an error line is showing.
 * It reads the height of one row and of everything that is not the list, then
 * divides the space a 92svh dialog is allowed to occupy by the first.
 *
 * Re-measures on resize, which is also what an orientation change looks like
 * on a tablet.
 */
function useMeasuredPageSize(
  dialog: React.RefObject<HTMLDivElement | null>,
  list: React.RefObject<HTMLUListElement | null>,
  ready: boolean,
): number {
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  /**
   * The smallest page size settled on at a given viewport height.
   *
   * WHY THIS EXISTS: measuring after every render is a feedback loop — fewer
   * rows makes the dialog shorter, a shorter dialog looks like room for more
   * rows, and the size oscillates forever. React notices before the user
   * does and tears the dialog down with "maximum update depth exceeded",
   * which is exactly what the first version of this did. Holding the size
   * non-increasing while the viewport is unchanged makes the sequence
   * monotonic, so it terminates — at worst at MIN_PAGE_SIZE. A resize clears
   * the record, because then there genuinely may be more room.
   */
  const settled = useRef<{ viewport: number; size: number } | null>(null);

  const measure = useCallback(() => {
    const dialogEl = dialog.current;
    const listEl = list.current;
    const row = listEl?.firstElementChild as HTMLElement | null;
    if (!dialogEl || !listEl || !row) return;

    const rowHeight = row.getBoundingClientRect().height;
    const gap = parseFloat(getComputedStyle(listEl).rowGap) || 0;
    const perRow = rowHeight + gap;
    if (perRow <= 0) return;

    // Everything that is not the list: head, tabs, any error line, the section
    // heading, "+ Item", the pager. scrollHeight rather than clientHeight, so
    // a dialog that is currently overflowing still reports its true content
    // height and the correction converges in one pass.
    const overhead = dialogEl.scrollHeight - listEl.getBoundingClientRect().height;
    const budget = window.innerHeight * 0.92 - overhead;

    const fits = Math.floor((budget + gap) / perRow);
    let next = Math.max(MIN_PAGE_SIZE, Math.min(MAX_PAGE_SIZE, fits));

    const viewport = window.innerHeight;
    const previous = settled.current;
    if (previous && previous.viewport === viewport) next = Math.min(next, previous.size);
    settled.current = { viewport, size: next };

    setSize((current) => (current === next ? current : next));
  }, [dialog, list]);

  // No dependency array: measure after EVERY render. What is on the page
  // changes the overhead — an error line appears, a section heading is one
  // line or two — and `setSize` is a no-op when the answer has not moved, so
  // this settles rather than loops.
  useLayoutEffect(() => {
    if (ready) measure();
  });

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return size;
}

export function MenuManager({ token, currency, onClose }: MenuManagerProps) {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [tabId, setTabId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  /**
   * A section to follow to its last page once the catalog comes back.
   *
   * A new item is appended to its section, which paging can put several taps
   * away — so adding one would look like nothing happened. This carries the
   * intent across the reload that follows the write.
   */
  const [followSection, setFollowSection] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [editing, setEditing] = useState<MenuItem | null>(null);
  const [adding, setAdding] = useState<{ categoryId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const response = await fetch("/api/catalog");
    if (!response.ok) {
      setError("Could not load the menu. Check the connection.");
      return;
    }
    const fresh = (await response.json()) as Catalog;
    setCatalog(fresh);
    setTabId((current) => current ?? fresh.categories.find((c) => !c.parentId)?.id ?? null);

    // Push the change to this terminal's own till immediately, rather than
    // waiting for the next till load to notice.
    await hydrateCatalog(fresh);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A tab change starts at that tab's first page, never at page four of a tab
  // that no longer has four pages.
  useEffect(() => setPage(0), [tabId]);

  const pageSize = useMeasuredPageSize(dialogRef, listRef, catalog !== null);

  /** Every write goes through here so one place handles auth, errors and reload. */
  const send = useCallback(
    async (method: string, path: string, body: unknown): Promise<boolean> => {
      setBusy(true);
      setError(null);
      try {
        const response = await fetch(`/api${path}`, {
          method,
          headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          const detail = (await response.json().catch(() => null)) as { message?: string } | null;
          setError(detail?.message ?? "That change was refused.");
          return false;
        }

        await load();
        return true;
      } catch {
        setError("No connection. Menu changes need one — they are shared with every terminal.");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [token, load],
  );

  const tabs = (catalog?.categories ?? [])
    .filter((c) => !c.parentId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const sections = (catalog?.categories ?? [])
    .filter((c) => c.parentId === tabId)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  /** A tab may hold items directly, exactly as the till renders it. */
  const groups = [
    ...(tabId ? [{ id: tabId, name: "", direct: true }] : []),
    ...sections.map((section) => ({ id: section.id, name: section.name, direct: false })),
  ];

  const pages = paginate(groups, catalog?.menuItems ?? [], pageSize);
  // The catalog reloads after every write, and a section can lose its last
  // page while you are standing on it. Clamping beats rendering a blank
  // dialog.
  const current = Math.min(page, Math.max(pages.length - 1, 0));
  const shown = pages[current];

  const followTarget = followSection
    ? pages.map((entry) => entry.group.id).lastIndexOf(followSection)
    : -1;

  useEffect(() => {
    if (followSection === null) return;
    if (followTarget >= 0) setPage(followTarget);
    setFollowSection(null);
  }, [followSection, followTarget]);

  if (!catalog) {
    return (
      <div className="menuadmin menuadmin--plain" role="dialog" aria-label="Manage the menu">
        <p className="menuadmin__loading">Loading the menu…</p>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="menuadmin" role="dialog" aria-label="Manage the menu" ref={dialogRef}>
      {/* Head and tabs travel together so ONE sticky element covers them. The
          dialog itself is the scroll container (menu-admin.css says why), and
          a second sticky bar would need to know the first one's height. */}
      <div className="menuadmin__top">
        <header className="menuadmin__head">
          <h2 className="menuadmin__title">Menu</h2>
          <button type="button" className="menuadmin__close" onClick={onClose}>
            Done
          </button>
        </header>

        <nav className="menuadmin__tabs" aria-label="Menu tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`menuadmin__tab${tab.id === tabId ? " is-active" : ""}`}
              onClick={() => setTabId(tab.id)}
            >
              {tab.name}
            </button>
          ))}
          <button
            type="button"
            className="menuadmin__tab menuadmin__tab--add"
            onClick={() => {
              const name = window.prompt("Name of the new tab");
              if (name) void send("POST", "/catalog/categories", { name });
            }}
          >
            + Tab
          </button>
        </nav>

        {error && (
          <p className="menuadmin__error" role="alert">
            {error}
          </p>
        )}
      </div>

      <div className="menuadmin__body">
        {shown ? (
          <section className="menuadmin__section">
            {shown.group.name && (
              <div className="menuadmin__section-head">
                <h3 className="menuadmin__section-title">
                  {shown.group.name}
                  {shown.parts > 1 && (
                    <span className="menuadmin__section-part">
                      {" "}
                      {shown.part} of {shown.parts}
                    </span>
                  )}
                </h3>
                <button
                  type="button"
                  className="menuadmin__rename"
                  onClick={() => {
                    const name = window.prompt("Rename this sub-category", shown.group.name);
                    if (name) void send("PATCH", `/catalog/categories/${shown.group.id}`, { name });
                  }}
                >
                  Rename
                </button>
              </div>
            )}

            <ul className="menuadmin__items" ref={listRef}>
              {shown.items.map((item) => {
                const sizes = catalog.variants
                  .filter((v) => v.menuItemId === item.id)
                  .sort((a, b) => a.sortOrder - b.sortOrder);

                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`menuadmin__item${item.available ? "" : " is-unavailable"}`}
                      onClick={() => setEditing(item)}
                    >
                      <span className="menuadmin__item-name">{item.name}</span>
                      <span className="menuadmin__item-prices">
                        {sizes.map((s) => formatMoney(money(s.price), currency)).join(" · ")}
                      </span>
                      {!item.available && <span className="menuadmin__item-flag">Sold out</span>}
                    </button>
                  </li>
                );
              })}
            </ul>

          </section>
        ) : (
          <p className="menuadmin__empty">Nothing on this tab yet.</p>
        )}
      </div>

      {/* PAGES, NOT SCROLLING. The list used to be one long scroller, and on
          the tablet it could not be moved at all — the menu simply looked
          frozen at whatever it was showing, which cost a shift four
          half-finished items saved one retry at a time. A page holds what
          fits, and the only gesture needed to reach the rest is a tap on a
          target that clears the touch minimum. */}
      <div className="menuadmin__foot">
        <div className="menuadmin__pager">
          <button
            type="button"
            className="menuadmin__page-step"
            onClick={() => setPage(current - 1)}
            disabled={current === 0}
          >
            ‹ Back
          </button>
          <span className="menuadmin__page-count" aria-live="polite">
            {pages.length === 0 ? "No pages" : `Page ${current + 1} of ${pages.length}`}
          </span>
          <button
            type="button"
            className="menuadmin__page-step"
            onClick={() => setPage(current + 1)}
            disabled={current >= pages.length - 1}
          >
            Next ›
          </button>
        </div>

        {/* ADDING IS ALWAYS ON SCREEN. "+ Item" first sat at the end of its
            section, which paging turned into "only on the last page" — the
            one page nobody is on when they decide to add something. It adds
            to the end of the section either way, so its place is here, beside
            the pager, on every page. */}
        <div className="menuadmin__actions">
          {shown && (
            <button
              type="button"
              className="menuadmin__add-item"
              onClick={() => setAdding({ categoryId: shown.group.id })}
            >
              + Item{shown.group.name ? ` in ${shown.group.name}` : ""}
            </button>
          )}

          <button
            type="button"
            className="menuadmin__add-section"
            disabled={!tabId}
            onClick={() => {
              const name = window.prompt("Name of the new sub-category");
              if (name && tabId) void send("POST", "/catalog/categories", { name, parentId: tabId });
            }}
          >
            + Sub-category
          </button>
        </div>
      </div>

      {adding && (
        <ItemForm
          title="New item"
          busy={busy}
          onCancel={() => setAdding(null)}
          onSubmit={async (name, sizes) => {
            const categoryId = adding.categoryId;
            const ok = await send("POST", "/catalog/items", {
              categoryId,
              name,
              variants: sizes.map((s) => ({ name: s.name, price: s.price })),
            });
            if (ok) {
              setAdding(null);
              // Land on the page the new item is actually on.
              setFollowSection(categoryId);
            }
          }}
        />
      )}

      {editing && (
        <ItemEditor
          item={editing}
          sizes={catalog.variants
            .filter((v) => v.menuItemId === editing.id)
            .sort((a, b) => a.sortOrder - b.sortOrder)}
          currency={currency}
          busy={busy}
          onClose={() => setEditing(null)}
          send={send}
        />
      )}
    </div>
  );
}

/* --- New item ------------------------------------------------------------ */

interface DraftSize {
  name: string;
  price: number;
}

function ItemForm({
  title,
  busy,
  onCancel,
  onSubmit,
}: {
  title: string;
  busy: boolean;
  onCancel(): void;
  onSubmit(name: string, sizes: DraftSize[]): void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [rows, setRows] = useState<Array<{ name: string; price: string }>>([
    { name: "Regular", price: "" },
  ]);
  const [problem, setProblem] = useState<string | null>(null);

  const submit = () => {
    if (!name.trim()) return setProblem("Give the item a name.");

    const sizes: DraftSize[] = [];
    for (const row of rows) {
      const price = toCentavos(row.price);
      if (price === null || row.price.trim() === "") {
        return setProblem("Every size needs a price.");
      }
      sizes.push({ name: row.name.trim() || "Regular", price });
    }

    setProblem(null);
    void onSubmit(name.trim(), sizes);
  };

  return (
    <div className="menuform" role="dialog" aria-label={title}>
      <h3 className="menuform__title">{title}</h3>

      <label className="menuform__field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </label>

      <div className="menuform__sizes">
        <span className="menuform__label">
          Sizes and prices
          {/* One size means one tap to sell. More than one opens the size sheet. */}
          <em> — one size sells in a single tap</em>
        </span>

        {rows.map((row, index) => (
          <div key={index} className="menuform__size">
            <input
              className="menuform__size-name"
              value={row.name}
              placeholder="Size"
              onChange={(e) =>
                setRows((r) => r.map((x, i) => (i === index ? { ...x, name: e.target.value } : x)))
              }
            />
            <input
              className="menuform__size-price"
              inputMode="decimal"
              value={row.price}
              placeholder="0.00"
              onChange={(e) =>
                setRows((r) => r.map((x, i) => (i === index ? { ...x, price: e.target.value } : x)))
              }
            />
            {rows.length > 1 && (
              <button
                type="button"
                aria-label={`Remove size ${index + 1}`}
                onClick={() => setRows((r) => r.filter((_, i) => i !== index))}
              >
                ×
              </button>
            )}
          </div>
        ))}

        <button
          type="button"
          className="menuform__add-size"
          onClick={() => setRows((r) => [...r, { name: "", price: "" }])}
        >
          + Size
        </button>
      </div>

      {problem && (
        <p className="menuform__error" role="alert">
          {problem}
        </p>
      )}

      <div className="menuform__actions">
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="is-primary" disabled={busy} onClick={submit}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

/* --- Existing item -------------------------------------------------------- */

function ItemEditor({
  item,
  sizes,
  currency,
  busy,
  onClose,
  send,
}: {
  item: MenuItem;
  sizes: Variant[];
  currency: CurrencyFormat;
  busy: boolean;
  onClose(): void;
  send(method: string, path: string, body: unknown): Promise<boolean>;
}) {
  const [name, setName] = useState(item.name);
  const [prices, setPrices] = useState<Record<string, string>>(
    Object.fromEntries(sizes.map((s) => [s.id, toPesos(s.price)])),
  );
  const [confirmingRetire, setConfirmingRetire] = useState(false);

  const save = async () => {
    if (name.trim() && name.trim() !== item.name) {
      await send("PATCH", `/catalog/items/${item.id}`, { name: name.trim() });
    }

    // Each changed price is its own version. Unchanged ones are skipped by the
    // endpoint anyway, but not sending them keeps the history clean of no-ops.
    for (const size of sizes) {
      const next = toCentavos(prices[size.id] ?? "");
      if (next !== null && next !== size.price) {
        await send("POST", "/catalog/prices", {
          targetType: "variant",
          targetId: size.id,
          amount: next,
        });
      }
    }

    onClose();
  };

  return (
    <div className="menuform" role="dialog" aria-label={`Edit ${item.name}`}>
      <h3 className="menuform__title">{item.name}</h3>

      <label className="menuform__field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>

      <div className="menuform__sizes">
        <span className="menuform__label">Prices</span>
        {sizes.map((size) => (
          <div key={size.id} className="menuform__size">
            <span className="menuform__size-fixed">{size.name}</span>
            <input
              className="menuform__size-price"
              inputMode="decimal"
              value={prices[size.id] ?? ""}
              aria-label={`Price for ${size.name}`}
              onChange={(e) => setPrices((p) => ({ ...p, [size.id]: e.target.value }))}
            />
            <span className="menuform__size-was">was {formatMoney(money(size.price), currency)}</span>
          </div>
        ))}
      </div>

      <div className="menuform__toggles">
        <button
          type="button"
          className="menuform__toggle"
          onClick={() => void send("PATCH", `/catalog/items/${item.id}`, {
            available: !item.available,
          })}
        >
          {item.available ? "Mark sold out" : "Back in stock"}
        </button>

        {/* FR-031: excludes this item from both the Senior/PWD discount and
            the VAT exemption — the eligible-line filter (eligibility.ts)
            reads exactly this flag off the priced line. */}
        <button
          type="button"
          className="menuform__toggle"
          onClick={() => void send("PATCH", `/catalog/items/${item.id}`, {
            statutoryDiscountEligible: !item.statutoryDiscountEligible,
          })}
        >
          {item.statutoryDiscountEligible
            ? "Eligible for Senior/PWD discount"
            : "Not eligible for Senior/PWD discount"}
        </button>

        <button
          type="button"
          className="menuform__retire"
          onClick={() => setConfirmingRetire(true)}
        >
          Take off the menu
        </button>
      </div>

      <div className="menuform__actions">
        <button type="button" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="is-primary" disabled={busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>

      {confirmingRetire && (
        <div className="menuform__confirm-backdrop">
          <ConfirmDestructive
            title={`Take ${item.name} off the menu?`}
            detail="Past sales keep it. This can be undone from here later."
            confirmLabel="Take off the menu"
            onCancel={() => setConfirmingRetire(false)}
            onConfirm={() => {
              // Retiring is reversible and keeps every past sale readable,
              // which is why there is no delete here at all.
              void send("PATCH", `/catalog/items/${item.id}`, { retired: true }).then(onClose);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default MenuManager;
